import { useMutation, useQuery } from "@apollo/client/react";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Box from "ds/components/Box";
import FormToggleField from "ds/components/Form/ToggleField";
import useTypedContext from "hooks/useTypedContext";

import {
  GET_API_KEYS_MANAGED_BY_NON_ROOT_ADMINS,
  SET_API_KEYS_MANAGED_BY_NON_ROOT_ADMINS,
} from "./gql";
import styles from "./styles.module.css";

type NonRootApiKeysToggleProps = {
  disabled?: boolean;
  tooltip?: string;
  plain?: boolean;
};

const NonRootApiKeysToggle = ({ disabled, tooltip, plain }: NonRootApiKeysToggleProps) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const { data, loading: isLoading } = useQuery(GET_API_KEYS_MANAGED_BY_NON_ROOT_ADMINS, {});

  const [mutate, { loading: isSaving }] = useMutation(SET_API_KEYS_MANAGED_BY_NON_ROOT_ADMINS, {
    onError,
    onCompleted: (mutationData) => {
      const enabled = mutationData.apiKeyEnableNonRootManagers;

      reportSuccess({
        message: `API keys management ${enabled ? "enabled" : "disabled"}`,
      });
    },
  });

  const toggle = useCallback(
    (isSelected: boolean) =>
      mutate({
        variables: { enable: isSelected },
        refetchQueries: [{ query: GET_API_KEYS_MANAGED_BY_NON_ROOT_ADMINS }],
        awaitRefetchQueries: true,
      }),
    [mutate]
  );

  return (
    <Box className={plain ? undefined : styles.managementToggle} fullWidth>
      <FormToggleField
        variant="switch"
        title="API keys management"
        description="Allow API keys management to Space level admins."
        checked={data?.apiKeysManagedByNonRootAdmins || false}
        onChange={toggle}
        disabled={disabled || isLoading || isSaving}
        isInitialValueLoading={isLoading && !data}
        tooltip={tooltip ? () => tooltip : undefined}
      />
    </Box>
  );
};

export default NonRootApiKeysToggle;
